import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Shows an inline spinner and disables the button while an action is in flight. */
  loading?: boolean;
  /**
   * Square icon-only button (no horizontal padding; width === height for the
   * size). Use with a single icon child and an `aria-label`.
   */
  iconOnly?: boolean;
  children?: ReactNode;
}

const baseClasses =
  'inline-flex items-center justify-center gap-2 rounded-md border font-medium ' +
  'transition-colors duration-base ease-standard active:scale-[0.99] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--field-focus-ring)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const variantClasses: Record<Variant, string> = {
  primary:
    'border-[var(--button-primary-border)] bg-[var(--button-primary-bg)] text-[var(--button-primary-text)] shadow-sm hover:bg-[var(--button-primary-bg-hover)]',
  secondary:
    'border border-[var(--border-strong)] text-[var(--text-primary)] bg-transparent hover:bg-[var(--surface-raised)]',
  ghost:
    'border-transparent text-[var(--text-primary)] bg-transparent hover:bg-[var(--surface-raised)]',
  danger:
    'border border-[var(--danger-border)] bg-[var(--danger-surface)] text-[var(--danger-text)] hover:bg-[var(--danger-surface-strong)]',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-base',
  lg: 'h-12 px-5 text-base',
};

/** Square dimensions for `iconOnly` buttons — width matches the size's height. */
const iconSizeClasses: Record<Size, string> = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    iconOnly = false,
    className,
    type,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(
        baseClasses,
        variantClasses[variant],
        iconOnly ? iconSizeClasses[size] : sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={16} /> : null}
      {children}
    </button>
  );
});
